import { type ReactNode, type PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { tapLight } from '../lib/haptics';

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** px of downward drag on the sheet header before release dismisses. */
const DISMISS_THRESHOLD = 96;

export function Modal({ title, onClose, children }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Swipe-to-dismiss (bottom-sheet mode only): drag the grab-handle/header —
  // or the body while it's scrolled to the top — down past the threshold and
  // release. Mouse and the centred desktop dialog (≥560px) keep the
  // tap-to-close affordances instead.
  const [dragY, setDragY] = useState(0);
  const [settling, setSettling] = useState(false);
  const dragFrom = useRef<number | null>(null);
  const dragging = useRef(false);
  const armed = useRef(false);
  const dismissing = useRef(false);
  // Drags starting in the scrollable body only count downward, so upward
  // swipes still scroll the content.
  const fromBody = useRef(false);

  const onHandleDown = (e: ReactPointerEvent) => {
    if (e.pointerType === 'mouse' || dismissing.current) return;
    if (!(window.matchMedia?.('(max-width: 559.98px)')?.matches ?? false)) return;
    dragFrom.current = e.clientY;
    dragging.current = false;
    armed.current = false;
    fromBody.current = false;
    setSettling(false);
  };

  const onBodyDown = (e: ReactPointerEvent) => {
    // Only when the body has nowhere left to scroll up — otherwise the
    // gesture is (or may become) a scroll and the sheet stays put.
    if ((bodyRef.current?.scrollTop ?? 0) > 0) return;
    onHandleDown(e);
    if (dragFrom.current != null) fromBody.current = true;
  };

  const onHandleMove = (e: ReactPointerEvent) => {
    if (dragFrom.current == null) return;
    const delta = e.clientY - dragFrom.current;
    if (!dragging.current) {
      // Ignore taps and jitters — only capture once it's clearly a drag, so
      // clicks on the close button (inside the header) still land. Body
      // drags must head downward; upward means the user is scrolling.
      if (fromBody.current ? delta < 8 : Math.abs(delta) < 8) return;
      dragging.current = true;
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    }
    const next = Math.max(0, delta);
    setDragY(next);
    if (next >= DISMISS_THRESHOLD && !armed.current) {
      armed.current = true;
      tapLight();
    } else if (next < DISMISS_THRESHOLD && armed.current) {
      armed.current = false;
    }
  };

  const onHandleUp = () => {
    if (dragFrom.current == null) return;
    dragFrom.current = null;
    dragging.current = false;
    if (armed.current) {
      // Fling the sheet off the bottom, then actually close.
      armed.current = false;
      dismissing.current = true;
      setSettling(true);
      setDragY(dialogRef.current?.offsetHeight ?? window.innerHeight);
      setTimeout(onClose, 200);
      return;
    }
    setSettling(true);
    setDragY(0);
  };

  // The browser reclaims the gesture (e.g. it became a scroll): spring back
  // without dismissing, whatever state the drag was in.
  const onHandleCancel = () => {
    if (dragFrom.current == null) return;
    dragFrom.current = null;
    dragging.current = false;
    armed.current = false;
    setSettling(true);
    setDragY(0);
  };

  // Move focus into the dialog on open; restore it to the opener on close.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => opener?.focus?.();
  }, []);

  // Close on Escape; keep Tab cycling inside the dialog (focus trap).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === dialogRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return createPortal(
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        ref={dialogRef}
        tabIndex={-1}
        style={{
          outline: 'none',
          transform: dragY ? `translateY(${dragY}px)` : undefined,
          transition: settling ? 'transform 0.28s cubic-bezier(0.16, 1, 0.3, 1)' : 'none',
        }}
      >
        <div
          className="modal-header"
          onPointerDown={onHandleDown}
          onPointerMove={onHandleMove}
          onPointerUp={onHandleUp}
          onPointerCancel={onHandleCancel}
        >
          <h2 className="modal-title" id="modal-title">
            {title}
          </h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div
          className="modal-body"
          ref={bodyRef}
          onPointerDown={onBodyDown}
          onPointerMove={onHandleMove}
          onPointerUp={onHandleUp}
          onPointerCancel={onHandleCancel}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
